import { POSITION_LABEL_MAX, UnitError, convert, monthOf, positionLabelOf, positionLabelProblem, prepareReading, targetFor, type MeasurementRecordV2, type Target } from '@gk/contracts'
import { localToInstant } from '../lib/time.ts'

/** M16-D: bunkers as readings (benchmark 37, GK-032; docs/M16.md D). A sand-depth walk is a set of readings of the notebook (M13), one per cell of the club's own
 *  grid (`position_label`, "A2", "B1"), of the floor and of the face. Everything here is pure and is a VIEW over the readings: the walks are the club days with a
 *  sand reading on the bunker, the grid comes from the labels, the verdict per cell from the club's own target of the month (Mesures › Objectifs; nothing is
 *  seeded), the comparison from the previous walk. Nothing is stored but the readings, so the same view serves a firmness or a green-quality walk once its types
 *  exist. Tested in bunkerUi.test.ts. No React, no clock (today and the zone are given). */

/** The two types of a walk, in the order the screen shows them: the floor (fond), then the face. */
export const SAND_TYPES = ['sand_depth_floor', 'sand_depth_face'] as const
export type SandType = (typeof SAND_TYPES)[number]
export const isSandType = (t: string): t is SandType => (SAND_TYPES as readonly string[]).includes(t)
/** The kind of area a walk is taken on: a code of `ref.area_type` (a type code, never a count or a name: the pins' precedent with the green). */
export const BUNKER_AREA_TYPE = 'bunker'
/** "Très bas": a cell below this share of the target's low bound. An app constant PRINTED on the section as the rule "(à confirmer)", like the thresholds of
 *  À traiter: it changes on request, not through a question. */
export const VERY_LOW_SHARE = 0.5
/** The quick grid's bounds (rows are letters A…, columns numbers 1…): a bunker walk of more cells adds them one by one. */
export const GRID_MAX_ROWS = 8
export const GRID_MAX_COLS = 8

export interface SandReading {
  id: string; measurement_type: string; area_id: string | null; value: number; observed_at: string; observed_local_date: string
  position_label?: string | null; superseded_by_id?: string | null; _pending?: boolean
}

// ---- the walks ------------------------------------------------------------------------------------------------------------------------------
/** The club days with a sand reading among `readings` (pass the readings of ONE area that count: a corrected reading is replaced by its correction), newest first. */
export function walkDays(readings: readonly SandReading[]): string[] {
  return [...new Set(readings.filter((r) => isSandType(r.measurement_type) && !r.superseded_by_id).map((r) => r.observed_local_date))].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
}

// ---- the cells -------------------------------------------------------------------------------------------------------------------------------
/** A label of the letter-and-number form ("A2", "b 12", "AA-3") as its row and column; anything else ("fond nord") is a cell outside the grid. */
export function parseCell(label: string): { row: string; col: number } | null {
  const m = /^([A-Za-z]{1,3})\s*-?\s*(\d{1,3})$/.exec(label.trim())
  return m ? { row: m[1]!.toUpperCase(), col: Number(m[2]) } : null
}
const rowOrder = (a: string, b: string) => a.length - b.length || (a < b ? -1 : a > b ? 1 : 0)
/** The grid's order: by row then column for the letter-and-number labels, then the club's other words in natural order. */
export function compareLabels(a: string, b: string): number {
  const pa = parseCell(a), pb = parseCell(b)
  if (pa && pb) return rowOrder(pa.row, pb.row) || pa.col - pb.col
  if (pa) return -1
  if (pb) return 1
  return a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' })
}

export type CellVerdict = 'in' | 'low' | 'veryLow' | 'high'
/** A cell against the target of its month: in the band; under its low bound ("bas"); under VERY_LOW_SHARE of it ("très bas"); over its high bound ("trop haut").
 *  No target, no verdict (the club's targets are the club's: nothing is called a standard). */
export function cellVerdict(value: number, target: Pick<Target, 'low' | 'high'> | undefined): CellVerdict | null {
  if (!target) return null
  if (target.low !== null && value < target.low) return value < target.low * VERY_LOW_SHARE ? 'veryLow' : 'low'
  if (target.high !== null && value > target.high) return 'high'
  return 'in'
}

export interface GridCell {
  /** The cell's label as stored; '' for the readings of the walk that name no cell. */
  label: string
  row: string | null; col: number | null
  /** The mean of the walk's readings of this cell (replicates), or null when the walk has none (the cell was read last time only). */
  value: number | null
  n: number
  verdict: CellVerdict | null
  /** The mean of the previous walk's readings of this cell, or null. */
  previous: number | null
  /** value − previous, in millimetres, when both exist. */
  delta: number | null
  pending: boolean
}
export interface GridSide {
  type: SandType
  /** The club's target for this type on this kind of area, in the walk's month. */
  target: Target | undefined
  /** The rows (letters) and columns (numbers) the letter-and-number cells use, in order: the screen lays `cells` out on them, a blank where none. */
  rows: string[]; cols: number[]
  cells: GridCell[]
  /** The cells named in the club's own words, outside the grid, in natural order. */
  others: GridCell[]
  /** The readings of the walk that name no cell (typed in the notebook's own form), as one cell with label '' — or null. */
  unlabelled: GridCell | null
  counts: Record<CellVerdict, number>
}
export interface BunkerGrid { walkDate: string | null; previousDate: string | null; sides: GridSide[] }

const mean = (xs: number[]): number => xs.reduce((s, x) => s + x, 0) / xs.length

/** The grid of a walk: for each type, a cell per label read on `walkDate` or on `previousWalk`, the walk's value (the mean of its replicates), its verdict
 *  against the target of the walk's month for `areaType`, and the previous walk's value beside. `readings` are the area's readings that count. */
export function bunkerGrid<T extends Target>(readings: readonly SandReading[], targets: readonly T[], walkDate: string | null, previousWalk: string | null, areaType: string = BUNKER_AREA_TYPE): BunkerGrid {
  const sides = SAND_TYPES.map((type): GridSide => {
    const target = walkDate ? targetFor(targets, type, areaType, monthOf(walkDate)) : undefined
    const of = (day: string | null) => (day ? readings.filter((r) => r.measurement_type === type && !r.superseded_by_id && r.observed_local_date === day) : [])
    const now = of(walkDate), before = of(previousWalk)
    const group = (rows: SandReading[]) => { const m = new Map<string, SandReading[]>(); for (const r of rows) { const k = positionLabelOf(r.position_label) ?? ''; const a = m.get(k); if (a) a.push(r); else m.set(k, [r]) } return m }
    const g = group(now), p = group(before)
    const counts: Record<CellVerdict, number> = { in: 0, low: 0, veryLow: 0, high: 0 }
    const cellOf = (label: string): GridCell => {
      const here = g.get(label) ?? [], last = p.get(label) ?? []
      const value = here.length ? mean(here.map((r) => r.value)) : null
      const previous = last.length ? mean(last.map((r) => r.value)) : null
      const verdict = value === null ? null : cellVerdict(value, target)
      if (verdict) counts[verdict]++
      const pc = label ? parseCell(label) : null
      return { label, row: pc?.row ?? null, col: pc?.col ?? null, value, n: here.length, verdict, previous, delta: value !== null && previous !== null ? value - previous : null, pending: here.some((r) => !!r._pending) }
    }
    const labels = [...new Set([...g.keys(), ...p.keys()])].filter((l) => l !== '').sort(compareLabels)
    const all = labels.map(cellOf)
    const cells = all.filter((c) => c.row !== null)
    const unlabelled = g.has('') ? cellOf('') : null
    return {
      type, target, cells, others: all.filter((c) => c.row === null), unlabelled, counts,
      rows: [...new Set(cells.map((c) => c.row!))].sort(rowOrder), cols: [...new Set(cells.map((c) => c.col!))].sort((a, b) => a - b),
    }
  })
  return { walkDate, previousDate: previousWalk, sides }
}

/** The cells of a walk that call for work (under the target, very low, or over it), per type, in the grid's order: what the corrective job's note names. */
export function cellsToWork(grid: BunkerGrid): Array<{ type: SandType; labels: string[] }> {
  return grid.sides.map((s) => ({ type: s.type, labels: [...s.cells, ...s.others].filter((c) => c.verdict !== null && c.verdict !== 'in').map((c) => c.label) })).filter((x) => x.labels.length > 0)
}

// ---- typing a walk ---------------------------------------------------------------------------------------------------------------------------
export interface WalkCellDraft { label: string; value: string }
export interface WalkDraft {
  /** The club day of the walk (today by default; a walk typed later from a sheet names its day). */
  day: string
  /** The unit every cell of this walk is typed in (mm, cm or in: ref.convert keeps the typed value beside the millimetres). */
  unit: string
  cells: Record<SandType, WalkCellDraft[]>
  method: string
}
/** The units a walk may be typed in (codes of ref.unit of the length dimension; the server converts, never the screen). */
export const WALK_UNITS = ['mm', 'cm', 'in'] as const

/** The cells a new walk offers on a side: every cell of the latest grid (the last walk's and the one before it, so a cell skipped once stays in the grid), in its order. */
export function draftLabels(grid: BunkerGrid, type: SandType): string[] {
  const s = grid.sides.find((x) => x.type === type)
  return s ? [...s.cells, ...s.others].map((c) => c.label) : []
}
/** A new walk starts from the cells of the latest grid of each type, every value empty: the person walks the same grid again. */
export function startDraft(grid: BunkerGrid, today: string): WalkDraft {
  const cells = Object.fromEntries(SAND_TYPES.map((type) => [type, draftLabels(grid, type).map((label) => ({ label, value: '' }))])) as Record<SandType, WalkCellDraft[]>
  return { day: today, unit: 'mm', cells, method: '' }
}

/** Release b review (finding 12): the walk form lays its inputs on the same rows × columns as the view (a person walking the bunker row by row types into
 *  the right box: in a 2 × 3 grid A3 is beside A2, never beside B1); the club's other words below, in their order. */
export interface FormLayout { rows: string[]; cols: number[]; at: Map<string, string>; others: string[] }
export function formLayout(labels: readonly string[]): FormLayout {
  const at = new Map<string, string>(); const rows = new Set<string>(); const cols = new Set<number>(); const others: string[] = []
  for (const l of [...labels].sort(compareLabels)) {
    const p = parseCell(l)
    if (p && !at.has(`${p.row}|${p.col}`)) { at.set(`${p.row}|${p.col}`, l); rows.add(p.row); cols.add(p.col) } else others.push(l)
  }
  return { rows: [...rows].sort(rowOrder), cols: [...cols].sort((a, b) => a - b), at, others }
}
/** A cell's last value in the unit the walk is typed in (display only: the units twin), the stored millimetres beside when the unit is another. */
export function lastValueText(mm: number, unit: string, symbol: (code: string) => string, fmt: (n: number, decimals: number) => string): string {
  if (unit === 'mm') return `${fmt(mm, 0)} ${symbol('mm')}`
  return `${fmt(Number(convert(mm, 'mm', unit)), unit === 'in' ? 2 : 1)} ${symbol(unit)} (${fmt(mm, 0)} ${symbol('mm')})`
}

/** The labels of a quick grid: `rows` letters × `cols` numbers ("A1", "A2", … "B1", …), within the bounds. */
export function quickGrid(rows: number, cols: number): string[] {
  const r = Math.max(1, Math.min(GRID_MAX_ROWS, Math.floor(rows))), c = Math.max(1, Math.min(GRID_MAX_COLS, Math.floor(cols)))
  const out: string[] = []
  for (let i = 0; i < r; i++) for (let j = 1; j <= c; j++) out.push(`${String.fromCharCode(65 + i)}${j}`)
  return out
}

/** A typed label as the walk keeps it: trimmed; a letter-and-number cell written the one way ("a 2", "A-2" → "A2"), so a walk and the next name it alike;
 *  the club's other words as typed. null for nothing. */
export function normaliseLabel(raw: string): string | null {
  const l = positionLabelOf(raw.trim())
  if (l === null) return null
  const p = parseCell(l)
  return p ? `${p.row}${p.col}` : l
}
export type AddLabelProblem = 'empty' | 'tooLong' | 'exists'
/** Whether a typed label can join a side of the walk: a name, at most 16 code points, not twice on one side (case and spaces aside). */
export function addLabelProblem(label: string, existing: readonly WalkCellDraft[]): AddLabelProblem | null {
  const l = normaliseLabel(label)
  if (l === null) return 'empty'
  if ([...l].length > POSITION_LABEL_MAX) return 'tooLong'
  return existing.some((c) => c.label.toLocaleLowerCase('fr') === l.toLocaleLowerCase('fr')) ? 'exists' : null
}
/** Adds labels to a side (normalised), in the grid's order, leaving out those it already holds. */
export function withLabels(cells: readonly WalkCellDraft[], labels: readonly string[]): WalkCellDraft[] {
  const out = [...cells]
  for (const raw of labels) { const l = normaliseLabel(raw); if (l !== null && addLabelProblem(l, out) === null) out.push({ label: l, value: '' }) }
  return out.sort((a, b) => compareLabels(a.label, b.label))
}

export interface WalkProblem { type: SandType | null; label: string | null; code: string }
export interface WalkCheck { ok: boolean; filled: number; problems: WalkProblem[] }
/** The form's own check, the twin of the server's in the person's words: each typed cell as a reading is checked (prepareReading: the value, the unit, the plausible
 *  range), its label as the handler checks it, and the walk's day (not after today, never a future walk). An empty cell is no reading; nothing typed is no walk. */
export function checkWalk(d: WalkDraft, today: string): WalkCheck {
  const problems: WalkProblem[] = []
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.day) || d.day > today) problems.push({ type: null, label: null, code: 'MEASUREMENT_DATE_INVALID' })
  let filled = 0
  for (const type of SAND_TYPES) for (const c of d.cells[type]) {
    if (c.value.trim() === '') continue
    filled++
    try { prepareReading({ type, value: c.value, unit: d.unit, hasArea: true }) } catch (e) {
      problems.push({ type, label: c.label, code: e instanceof UnitError ? e.code : ((e as { code?: string }).code ?? 'MEASUREMENT_VALUE_INVALID') }); continue
    }
    const lp = positionLabelProblem(c.label, true)
    if (lp) problems.push({ type, label: c.label, code: lp })
  }
  return { ok: filled > 0 && problems.length === 0, filled, problems }
}

/** The commands of a walk: one `measurement.record@2` per typed cell, the floor first, in the grid's order. A walk of today is sent as "now" (no instant: the
 *  command's own); a walk of an earlier day at noon of that club day (the day is what matters, never a made-up hour: the import's rule). */
export function walkPayloads(d: WalkDraft, o: { areaId: string; today: string; tz: string; newId: () => string }): MeasurementRecordV2[] {
  const at = d.day === o.today ? undefined : localToInstant(d.day, '12:00', o.tz)
  const method = d.method.trim()
  const out: MeasurementRecordV2[] = []
  for (const type of SAND_TYPES) for (const c of [...d.cells[type]].sort((a, b) => compareLabels(a.label, b.label))) {
    if (c.value.trim() === '') continue
    out.push({
      measurement_id: o.newId(), measurement_type: type, area_id: o.areaId, value: Number(c.value.trim().replace(',', '.')), unit: d.unit,
      position_label: positionLabelOf(c.label), ...(at ? { observed_at: at } : {}), ...(method ? { method } : {}),
    })
  }
  return out
}
